import {
  getMeta,
  fetchHubCategories,
  countVacantSubordinates,
  vacantSubordinates
} from '@/services/meta-api.service.js'
import { getBoard, updateBoard } from '@/services/boards-api.service'
import { getSettings, updateSettings } from '@/services/settings-api.service'
import { getAllowedValues, updateAllowedValues } from '@/services/allowed-values-api.service'
import { keyBy } from 'lodash-es'
import { logger } from '@/utils/logger'

// This module is inteded to store the meta data of the main board.
// Main usecase is to return the statistics of various fields
// such as number of people, departments, locations, and unique custom fields
const getDefaultState = () => {
  return {
    peopleMetaData: {},
    // number of people in the board
    numbPeople: 0,
    departments: [],
    locations: [],
    theme: {},
    hubCategories: {},
    roles: [],
    allowedValues: {},
    directManagerVacancies: {},
    noManagerVacancies: {},
    rollupManagerVacancies: {},
    vacancies: [],
    duplicationPresets: {},
    fieldMandatory: {},
    fieldRestrictDropdownInputs: {},
    fieldGroups: {},
    groupOrder: []
  }
}

const state = getDefaultState()

const getHubCategoriesCacheKey = (boardId, includeCustomFieldTypes = []) => {
  const normalizedTypes = [...new Set(includeCustomFieldTypes)]
    .filter(Boolean)
    .sort((typeA, typeB) => typeA.localeCompare(typeB))
  if (!normalizedTypes.length) return boardId
  return `${boardId}:include-custom-field-types:${normalizedTypes.join(',')}`
}

const mutations = {
  SET_PEOPLE_META_DATA(_state, data) {
    _state.peopleMetaData = data
  },
  SET_NUMB_PEOPLE(_state, numbPeople) {
    _state.numbPeople = numbPeople
  },
  SET_DEPARTMENTS(_state, departments) {
    _state.departments = departments
  },
  SET_MANAGERS(_state, managers) {
    _state.managers = managers
  },
  SET_LOCATIONS(_state, locations) {
    _state.locations = locations
  },
  SET_ORG_DEPTH(_state, depth) {
    _state.depth = depth
  },
  SET_THEME(_state, theme) {
    if (theme) {
      _state.theme = theme
    }
  },
  SET_COLOUR(_state, { content, filter, colour }) {
    _state.theme[content][filter] = colour
  },
  SET_ALLOWED_VALUES(_state, allowedValues) {
    _state.allowedValues = allowedValues
  },
  RESET_SYNC_MODULE(_state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(_state, getDefaultState())
  },
  SET_HUB_CATEGORIES(_state, { boardId, includeCustomFieldTypes = [], data }) {
    _state.hubCategories = _state.hubCategories ?? {}
    _state.hubCategories[getHubCategoriesCacheKey(boardId, includeCustomFieldTypes)] = data
  },
  CLEAR_HUB_CATEGORIES(_state, { boardId }) {
    if (!boardId || !_state.hubCategories) return

    const variantPrefix = `${boardId}:include-custom-field-types:`
    const nextHubCategories = { ..._state.hubCategories }
    Object.keys(nextHubCategories).forEach((key) => {
      if (key === boardId || key.startsWith(variantPrefix)) {
        delete nextHubCategories[key]
      }
    })
    _state.hubCategories = nextHubCategories
  },
  SET_ROLES(_state, roles) {
    _state.roles = roles
  },
  SET_DUPLICATION_PRESETS(_state, duplicationPresets) {
    _state.duplicationPresets = duplicationPresets
  },
  SET_FIELD_MANDATORY(_state, fieldMandatory) {
    _state.fieldMandatory = fieldMandatory
  },
  SET_FIELD_RESTRICT_DROPDOWN_INPUTS(_state, fieldRestrictDropdownInputs) {
    _state.fieldRestrictDropdownInputs = fieldRestrictDropdownInputs
  },
  SET_DIRECT_MANAGER_VACANCIES(_state, managerVacancies) {
    _state.directManagerVacancies = managerVacancies
  },
  SET_NO_MANAGER_VACANCIES(_state, noManagerVacancies) {
    _state.noManagerVacancies = noManagerVacancies
  },
  SET_ROLLUP_MANAGER_VACANCIES(_state, managerVacancies) {
    _state.rollupManagerVacancies = managerVacancies
  },
  SET_VACANCIES(_state, vacancies) {
    _state.vacancies = vacancies
  },
  SET_FIELD_GROUPS(_state, { fieldGroups }) {
    _state.fieldGroups = fieldGroups
  },
  SET_GROUP_ORDER(_state, groupOrder) {
    _state.groupOrder = groupOrder || []
  }
}

const getters = {
  getMeta: (_state) => {
    return _state.peopleMetaData
  },
  getNumbPeople: (_state) => {
    return _state.numbPeople
  },
  getDepartments: (_state) => {
    return _state.departments
  },
  getLocations: (_state) => {
    return _state.locations
  },
  getRoles: (_state) => {
    return _state.roles
  },
  getDepth: (_state) => {
    return _state.depth
  },
  getManagers: (_state) => {
    return _state.managers
  },
  getHubCategories:
    (_state) =>
    (boardId, { includeCustomFieldTypes = [] } = {}) =>
      _state.hubCategories?.[getHubCategoriesCacheKey(boardId, includeCustomFieldTypes)] ?? [],
  getDuplicationPresets: (_state) => _state.duplicationPresets,
  getFieldMandatory: (_state) => _state.fieldMandatory,
  getFieldRestrictDropdownInputs: (_state) => _state.fieldRestrictDropdownInputs,
  getAllowedValues: (_state) => {
    return _state.allowedValues
  },
  getDirectManagerVacancies: (_state) => {
    return _state.directManagerVacancies
  },
  getNoManagerVacancies: (_state) => {
    return _state.noManagerVacancies
  },
  getRollupManagerVacancies: (_state) => {
    return _state.rollupManagerVacancies
  },
  getVacancies: (_state) => {
    return _state.vacancies
  },
  getFieldGroups: (_state) => {
    return _state.fieldGroups
  },
  getGroupOrder: (_state) => {
    return _state.groupOrder || []
  }
}

const actions = {
  async fetchPeopleMetaData(context, { boardId }) {
    const meta = await getMeta({ boardId })
    context.commit('SET_NUMB_PEOPLE', meta.numbPeople)
    context.commit('SET_DEPARTMENTS', meta.departments)
    context.commit('SET_LOCATIONS', meta.locations)
    context.commit('SET_MANAGERS', meta.managers)
    context.commit('SET_ORG_DEPTH', meta.depth)
    context.commit('SET_ROLES', meta.roles)
  },

  async fetchVacancies(context, { boardId }) {
    const vacancies = await vacantSubordinates({ boardId })
    context.commit('SET_VACANCIES', vacancies)
  },

  async countVacancies(context, { boardId, filters }) {
    try {
      const count = await countVacantSubordinates({ boardId, filters })
      if (count) {
        context.commit('SET_DIRECT_MANAGER_VACANCIES', count.direct)
        context.commit('SET_ROLLUP_MANAGER_VACANCIES', count.rollup)
        context.commit('SET_NO_MANAGER_VACANCIES', count.noManager)
      }
    } catch (e) {
      logger.error('countVacancies err', e.message)
    }
  },

  async fetchDuplicationPresets(context, { boardId }) {
    const boardData = await getBoard({ boardId })
    const duplicationPresets = boardData.duplicationPresets
    context.commit('SET_DUPLICATION_PRESETS', duplicationPresets)
  },

  async fetchFieldMandatory(context, { boardId }) {
    const boardData = await getBoard({ boardId })
    const fieldMandatory = boardData.fieldMandatory
    context.commit('SET_FIELD_MANDATORY', fieldMandatory)
  },

  async fetchFieldRestrictDropdownInputs(context, { boardId }) {
    try {
      const boardData = await getBoard({ boardId })
      const fieldRestrictDropdownInputs = boardData.fieldRestrictDropdownInputs
      context.commit('SET_FIELD_RESTRICT_DROPDOWN_INPUTS', fieldRestrictDropdownInputs)
    } catch (error) {
      logger.error('Failed to fetch field restrictions:', error.message)
      context.commit('SET_FIELD_RESTRICT_DROPDOWN_INPUTS', {})
    }
  },

  async fetchAllowedValues(context, { boardId }) {
    const allowedValues = await getAllowedValues({ boardId })
    context.commit('SET_ALLOWED_VALUES', keyBy(allowedValues, 'key'))
  },

  async updateTheme(context, { boardId, theme }) {
    const boardData = await getBoard({
      boardId
    })
    context.commit('SET_THEME', theme)
    await updateBoard({ boardId, payload: { ...boardData, theme } })
  },

  async updateDuplicationPresets(context, { boardId, duplicationPresets }) {
    const boardData = await getBoard({
      boardId
    })
    context.commit('SET_DUPLICATION_PRESETS', duplicationPresets)
    await updateBoard({ boardId, payload: { ...boardData, duplicationPresets } })
  },

  async updateFieldMandatory(context, { boardId, fieldMandatory }) {
    const boardData = await getBoard({
      boardId
    })
    context.commit('SET_FIELD_MANDATORY', fieldMandatory)
    await updateBoard({ boardId, payload: { ...boardData, fieldMandatory } })
  },

  async updateFieldRestrictDropdownInputs(context, { boardId, fieldRestrictDropdownInputs }) {
    const boardData = await getBoard({
      boardId
    })
    context.commit('SET_FIELD_RESTRICT_DROPDOWN_INPUTS', fieldRestrictDropdownInputs)
    await updateBoard({ boardId, payload: { ...boardData, fieldRestrictDropdownInputs } })
  },

  async updateAllowedValues(context, { boardId, allowedValues }) {
    context.commit('SET_ALLOWED_VALUES', allowedValues)
    await updateAllowedValues({ boardId, payload: { allowedValues } })
  },

  setColour(context, { content, filter, colour }) {
    context.commit('SET_COLOUR', { content, filter, colour })
  },

  async fetchFieldGroups(context, { boardId }) {
    const boardData = await getSettings({ boardId })
    const fieldGroups = boardData.fieldGroups
    const groupOrder = boardData.groupOrder
    context.commit('SET_FIELD_GROUPS', { fieldGroups })
    context.commit('SET_GROUP_ORDER', groupOrder)
  },

  async updateFieldGroups(context, { boardId, fieldGroups, groupOrder }) {
    context.commit('SET_FIELD_GROUPS', { fieldGroups })
    context.commit('SET_GROUP_ORDER', groupOrder)
    await updateSettings({ boardId, settings: { fieldGroups, groupOrder } })
  },

  clearHubCategories({ commit }, { boardId }) {
    commit('CLEAR_HUB_CATEGORIES', { boardId })
  },

  async fetchHubCategories(
    { commit, getters: contextGetters, rootGetters },
    { boardId, force = false, includeCustomFieldTypes = [] }
  ) {
    if (
      !force &&
      contextGetters.getHubCategories(boardId, { includeCustomFieldTypes })?.length > 0
    ) {
      return
    }

    const visibleFields = rootGetters['getFieldVisibility'] || {}
    const hasVisibilitySettings = Object.keys(visibleFields).length > 0

    try {
      const result = await fetchHubCategories({ boardId, includeCustomFieldTypes })
      commit('SET_HUB_CATEGORIES', {
        boardId,
        includeCustomFieldTypes,
        data:
          result.filter(
            (category) => visibleFields[category.id] === true || !hasVisibilitySettings
          ) || []
      })
    } catch (e) {
      logger.error('fetchHubCategories err', e.message)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {}
}
